/**
 * Interactions (phase 1) — WRITE request schemas for `/api/v1/interactions`.
 *
 * The create / update bodies ARE the payload of the two Pattern B+ RPCs in
 * `20260928170000_interactions.sql`:
 *
 *   create_interaction(p_organization_id uuid, p_payload jsonb) → uuid
 *   update_interaction(p_interaction_id  uuid, p_payload jsonb) → uuid
 *
 * with the keys `interaction_type_id, title, occurred_on, project_id, place_id,
 * notes, participants[{person_id, role_id, note, follow_up_on}]` — plus ONE
 * handler-only key per participant, `business_id`, which the route consumes
 * (it links the person to that business through `upsert_business_contact`)
 * and which is NEVER sent to the RPC. Build the RPC payload with
 * `toInteractionRpcPayload`, never by passing the parsed body through.
 *
 * Every object is `.strict()` (zod silently strips unknown keys otherwise, so
 * a body carrying `organization_id` would 200 with the key dropped).
 *
 * DELIBERATELY ABSENT: `organization_id` (from `:orgId`, pinned by the RPC),
 * `code` (`INT-{sequence}` trigger), `created_by` (`auth.uid()`, pinned by the
 * RPC and the INSERT policy), and every `follow_up_done_at` /
 * `follow_up_notified_at` (the toggle route and the reminder job own them).
 *
 * The RPC contract these mirror (and which the schemas catch first, so the
 * API rarely sees the DB codes):
 *   - `participants` is a NON-EMPTY array whenever it is present — on create
 *     it is required, on update it is optional but `.min(1)` when present
 *     (the RPC raises 22023 on an empty array in both).
 *   - a person may appear once (the RPC raises 23505).
 *   - `title` is not blank (CHECK `interactions_title_not_blank`, 23514).
 *   - on update, `participants` is the COMPLETE desired set, diffed by
 *     `person_id`; within an element an ABSENT `role_id` / `note` /
 *     `follow_up_on` means NULL. `toInteractionRpcPayload` therefore spells
 *     every participant key out (null when unset) so the wire says what it
 *     means.
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
/** Participants per interaction (soft cap — a conference table, not a mailing list). */
export const INTERACTION_MAX_PARTICIPANTS = 100;
export const INTERACTION_TITLE_MAX = 255;
export const INTERACTION_NOTES_MAX = 10_000;
export const INTERACTION_PARTICIPANT_NOTE_MAX = 2_000;
// ---------------------------------------------------------------------------
// Field helpers
// ---------------------------------------------------------------------------
/** `''` / whitespace-only → `null`; anything else passes through untouched. */
const blankToNull = (value) => typeof value === "string" && value.trim() === "" ? null : value;
/** Optional, nullable free text; `''` clears the column. */
const optionalText = (max) => z.preprocess(blankToNull, z.string().trim().max(max).nullable()).optional();
const uuid = z.string().uuid();
const optionalNullableUuid = uuid.nullable().optional();
const titleSchema = z.string().trim().min(1).max(INTERACTION_TITLE_MAX);
// ---------------------------------------------------------------------------
// Participants
// ---------------------------------------------------------------------------
/**
 * One participant row. `business_id` is HANDLER-ONLY: the organization the
 * person was met as. The route links the person to it (when not yet linked)
 * after the RPC succeeds; it is not stored on the interaction.
 */
export const interactionParticipantInputSchema = z
    .object({
    person_id: uuid,
    role_id: optionalNullableUuid,
    note: optionalText(INTERACTION_PARTICIPANT_NOTE_MAX),
    follow_up_on: z.preprocess(blankToNull, calendarDateSchema.nullable()).optional(),
    business_id: optionalNullableUuid,
})
    .strict();
const DUPLICATE_PERSON_MESSAGE = "a person may appear only once in participants";
/** Index of every participant whose `person_id` already appeared earlier in the list. */
export function duplicateParticipantIndexes(participants) {
    const seen = new Set();
    const dupes = [];
    participants.forEach((p, index) => {
        const id = p.person_id;
        if (!id)
            return;
        if (seen.has(id))
            dupes.push(index);
        else
            seen.add(id);
    });
    return dupes;
}
/**
 * The participants array: 1 … `INTERACTION_MAX_PARTICIPANTS`, no person twice.
 * The duplicate issue is raised on each repeated row's `person_id`, so a form
 * can show it inline next to the offending picker.
 */
const participantsSchema = z
    .array(interactionParticipantInputSchema)
    .min(1)
    .max(INTERACTION_MAX_PARTICIPANTS)
    .superRefine((rows, ctx) => {
    for (const index of duplicateParticipantIndexes(rows)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: DUPLICATE_PERSON_MESSAGE,
            path: [index, "person_id"],
        });
    }
});
// ---------------------------------------------------------------------------
// Create / update
// ---------------------------------------------------------------------------
/** POST `/orgs/:orgId/interactions`. */
export const createInteractionSchema = z
    .object({
    interaction_type_id: uuid,
    title: titleSchema,
    occurred_on: calendarDateSchema,
    project_id: optionalNullableUuid,
    place_id: optionalNullableUuid,
    notes: optionalText(INTERACTION_NOTES_MAX),
    participants: participantsSchema,
})
    .strict();
/**
 * PATCH `/orgs/:orgId/interactions/:id` — PRESENCE-KEYED (a header column
 * moves only when its key is present; `null` clears the nullable ones).
 * `interaction_type_id`, `title` and `occurred_on` are NOT NULL columns, so
 * they take no `null`. `participants`, when present, is the complete set.
 */
export const updateInteractionSchema = z
    .object({
    interaction_type_id: uuid.optional(),
    title: titleSchema.optional(),
    occurred_on: calendarDateSchema.optional(),
    project_id: optionalNullableUuid,
    place_id: optionalNullableUuid,
    notes: optionalText(INTERACTION_NOTES_MAX),
    participants: participantsSchema.optional(),
})
    .strict()
    .refine((value) => Object.values(value).some((v) => v !== undefined), {
    message: "At least one field must be provided",
});
/** PATCH `/orgs/:orgId/participants/:participantId/follow-up` — mark done / reopen. */
export const interactionFollowUpToggleSchema = z.object({ done: z.boolean() }).strict();
const HEADER_KEYS = [
    "interaction_type_id",
    "title",
    "occurred_on",
    "project_id",
    "place_id",
    "notes",
];
/**
 * The RPC payload of a PARSED create / update body: the header keys that are
 * present (absent stays absent — the update is presence-keyed), and each
 * participant with all four RPC keys (unset → `null`, which is what an absent
 * key means to `update_interaction` anyway) and WITHOUT `business_id`.
 */
export function toInteractionRpcPayload(input) {
    const payload = {};
    for (const key of HEADER_KEYS) {
        const value = input[key];
        if (value !== undefined)
            payload[key] = value;
    }
    if (input.participants !== undefined) {
        payload.participants = input.participants.map((p) => ({
            person_id: p.person_id,
            role_id: p.role_id ?? null,
            note: p.note ?? null,
            follow_up_on: p.follow_up_on ?? null,
        }));
    }
    return payload;
}
/** The participants that carry a `business_id`, in payload order. */
export function interactionBusinessLinks(input) {
    return (input.participants ?? []).flatMap((p) => p.business_id ? [{ person_id: p.person_id, business_id: p.business_id }] : []);
}
//# sourceMappingURL=interactions.js.map